import { queueOwnerId, belongsToCurrentUser } from './queue-owner.js';
/**
 * Sync engine — выгребает outbox по одной мутации, отправляет на API.
 * - При сетевой ошибке: stop, ждём online.
 * - При 4xx (кроме 408/429): помечаем failed, требуется ручное решение.
 * - При 5xx / 408 / 429: backoff retry.
 * - Идемпотентно: каждая мутация имеет idempotencyKey.
 */
import { db, type OutboxItem } from './db/schema.js';
import { api, HttpError } from './api.js';
import { flushPhotos } from './photoSync.js';
import { orderIdOfUrl } from './pending-order-overlay.js';
import { queryClient } from './queryClient.js';

type SyncListener = (state: SyncState) => void;
export interface SyncState {
  pending: number;
  inFlight: boolean;
  failed: number;
  online: boolean;
}

let listeners = new Set<SyncListener>();

/** Карточка заказа накладывает очередь поверх ответа сервера — перечитываем её при каждом изменении очереди. */
function refreshOrder(url: string) {
  const orderId = orderIdOfUrl(url);
  if (orderId) void queryClient.invalidateQueries({ queryKey: ['order', orderId] });
}
let inFlight = false;

export function subscribe(fn: SyncListener): () => void {
  listeners.add(fn);
  void notify();
  return () => listeners.delete(fn);
}

async function notify() {
  const [pendingOutbox, failedOutbox, pendingPhotos, failedPhotos] = await Promise.all([
    db.outbox.where('status').anyOf('pending', 'in_flight').filter(belongsToCurrentUser).count(),
    db.outbox.where('status').equals('failed').filter(belongsToCurrentUser).count(),
    db.photos.where('status').anyOf('pending', 'uploading').filter(belongsToCurrentUser).count(),
    db.photos.where('status').equals('failed').filter(belongsToCurrentUser).count(),
  ]);
  const state: SyncState = {
    // Считаем и обычные мутации, и фото — пользователю важен общий «висяк»
    pending: pendingOutbox + pendingPhotos,
    inFlight,
    failed: failedOutbox + failedPhotos,
    online: navigator.onLine,
  };
  listeners.forEach((fn) => fn(state));
}

export async function enqueue(item: Omit<OutboxItem, 'id' | 'createdAt' | 'attempts' | 'lastError' | 'status'>): Promise<number> {
  const id = await db.outbox.add({
    ...item,
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
    status: 'pending',
  });
  void notify();
  refreshOrder(item.url);
  // Если онлайн — пробуем сразу прокачать
  if (navigator.onLine) void flush();
  return id as number;
}

let stopRequested = false;

export async function flush(): Promise<void> {
  if (inFlight) return;
  if (!navigator.onLine) return;
  const ownerId = queueOwnerId();
  if (!ownerId) return;

  inFlight = true;
  stopRequested = false;
  void notify();

  try {
    while (!stopRequested) {
      if (queueOwnerId() !== ownerId) break;
      const next = await db.transaction('rw', db.outbox, async () => {
        const candidate = await db.outbox.where('ownerId').equals(ownerId)
          .filter((item) => item.status === 'pending' || item.status === 'in_flight')
          .sortBy('createdAt').then((items) => items[0]);
        if (!candidate || candidate.id == null) return undefined;
        if (candidate.status === 'in_flight' && (candidate.leaseUntil ?? 0) > Date.now()) return undefined;
        await db.outbox.update(candidate.id, { status: 'in_flight', leaseUntil: Date.now() + 120_000, attempts: candidate.attempts + 1 });
        return candidate;
      });
      if (!next || next.id == null) break;
      try {
        await api(next.url, {
          method: next.method,
          body: next.body !== undefined ? JSON.stringify(next.body) : undefined,
          idempotencyKey: next.idempotencyKey,
          expectedUserId: ownerId,
        });
        await db.outbox.delete(next.id);
        void notify();
        refreshOrder(next.url);
      } catch (e) {
        const isHttp = e instanceof HttpError;
        const status = isHttp ? e.status : 0;
        const isPermFail = isHttp && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;

        await db.outbox.update(next.id, {
          status: isPermFail ? 'failed' : 'pending',
          lastError: (e as Error).message,
        });
        void notify();
        // Отклонённая сервером запись больше не накладывается — карточка покажет правду.
        if (isPermFail) refreshOrder(next.url);

        if (!isPermFail) {
          // Сетевая или 5xx — стоп, ждём изменения сети
          break;
        }
        // 4xx — пропускаем, идём к следующему
      }
    }
  } finally {
    inFlight = false;
    void notify();
  }
}

/** Прогнать обе очереди: мутации (outbox) и фото. */
function flushAll() {
  void flush();
  void flushPhotos();
}

export function startSyncEngine() {
  // Триггеры: онлайн, видимость вкладки, периодика 30 сек
  window.addEventListener('online', () => { flushAll(); void notify(); });
  window.addEventListener('offline', () => { stopRequested = true; void notify(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      flushAll();
    }
  });
  // Периодика — 30 сек, на случай если вкладка живёт долго
  setInterval(() => {
    if (navigator.onLine && !inFlight) flushAll();
  }, 30_000);

  // Первый прогон сразу при старте
  if (navigator.onLine) flushAll();
}

/** Удалить failed-задачу (после ручного разбора пользователем). */
export async function dropFailed(id: number) {
  const item = await db.outbox.get(id);
  if (item && belongsToCurrentUser(item)) await db.outbox.delete(id);
  void notify();
}

/** Список failed для UI. */
export async function listFailed(): Promise<OutboxItem[]> {
  return db.outbox.where('status').equals('failed').filter(belongsToCurrentUser).toArray();
}
